import { useEffect, useMemo, useState } from 'react';
import axios from '@/_utils/axios';
import OverflowMenuItem from '@/components/_common/OverflowMenu/OverflowMenuItem';
import { useToast } from '@/components/_common/Toast';
import { OverflowMenu, PolarisIcon } from '@/exports/columnCommonComponents';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectIncrementalSchedulingDeleteStatusBySchedulingId } from '@/redux/selectors/focusScheduleSelectors';
import { selectFocusNewScheduleSlice } from '@/redux/selectors/focusNewScheduleSelectors';
import { deleteIncrementalScheduling, fetchIncrementalSchedulingsByVersionSchedulingId } from '@/redux/slices/focusScheduleSlice';
import { FocusScheduling, FocusVersionScheduling } from '@/redux/slices/slice_types';
import EditIncrementalLaunchModal from '../IncrementalLaunchModal/EditIncrementalLaunchModal';
import { LaunchModalMode } from '../IncrementalLaunchModal/types';
import { getFocusRightsValidateBySchedulingId } from '@/redux/slices/focusNewScheduleSlice';
import { getData } from '@/redux/slices/dataTableSlice';

type AvailScheduleRowExpandOverflowActionsProps = {
  row: FocusScheduling;
  versionScheduling: FocusVersionScheduling;
  launchMode?: LaunchModalMode; // NATALIA: Need logic to define launch mode from the launch row
};

const AvailScheduleRowExpandOverflowActions = ({
  row,
  versionScheduling,
  launchMode = LaunchModalMode.Add
}: AvailScheduleRowExpandOverflowActionsProps) => {
  const { success, error, info } = useToast();
  const dispatch = useAppDispatch();

  const { rightsValidateBySchedulingId } = useAppSelector(selectFocusNewScheduleSlice);

  const deleteStatus = useAppSelector((state) => selectIncrementalSchedulingDeleteStatusBySchedulingId(state, row.id));

  const [fetchingRights, setFetchingRights] = useState(false);
  const [isEditIncrementalLaunchModalOpen, setIsEditIncrementalLaunchModalOpen] = useState(false);

  const rightsValidate = useMemo(() => rightsValidateBySchedulingId[row.id], [rightsValidateBySchedulingId, row.id]);

  const isDraft = row.status === 'draft';
  const isActive = row.status === 'confirmed';

  const handleDelete = () => {
    info(`Deleting Scheduling ${row.id}`);

    // TODO: Figure out
    dispatch(
      deleteIncrementalScheduling({
        schedulingId: row.id,
        versionSchedulingId: versionScheduling.id
      })
    );
  };

  const submitScheduling = () => {
    axios
      .post('focus/scheduling/submit/', { ids: [row.id] }, { baseURL: '/api/v1.1' })
      .then(() => {
        success('Scheduling submitted successfully');
        dispatch(getData());
        dispatch(fetchIncrementalSchedulingsByVersionSchedulingId({ versionSchedulingId: versionScheduling.id }));
        setFetchingRights(false);
      })
      .catch(() => {
        error('Error submitting scheduling');
        setFetchingRights(false);
      });
  };

  const getValidate = () => {
    setFetchingRights(true);
    dispatch(
      getFocusRightsValidateBySchedulingId({
        schedulingId: row.id,
        title_id: [versionScheduling.version.id],
        license: [versionScheduling.license],
        availed_platform: [versionScheduling.platform],
        term_start__gte: row.window_start,
        term_end__lt: row.window_end,
        territories: row.territory,
        audio: row.audio_languages.map((language) => ({
          element_subtype: 'full',
          language: language.toLowerCase()
        })),
        timedtext: row.timed_text_languages.map((language) => ({
          element_subtype: 'full-mix',
          language: language.toLowerCase()
        }))
      })
    );
  };

  useEffect(() => {
    if (deleteStatus === 'success') {
      success('Scheduling deleted successfully');
    }

    if (deleteStatus === 'error') {
      error('Error deleting scheduling');
    }
  }, [deleteStatus, success, error]);

  useEffect(() => {
    if (!rightsValidate) return setFetchingRights(false);
    if (!fetchingRights) return;

    if (rightsValidate === 'valid') {
      success('Rights validated successfully');
      submitScheduling();
      return;
    }

    error('Rights validation failed. Please review the schedule details and try again.');
    setFetchingRights(false);
  }, [rightsValidate]);

  return (
    <>
      <OverflowMenu>
        <OverflowMenuItem onClick={() => setIsEditIncrementalLaunchModalOpen(true)}>
          <PolarisIcon icon="edit_outlined" height={12} />
          Edit
        </OverflowMenuItem>
        {isActive && (
          <OverflowMenuItem>
            <PolarisIcon icon="delete_outline" height={12} />
            Takedown
          </OverflowMenuItem>
        )}
        {isDraft && (
          <OverflowMenuItem onClick={getValidate} disabled={fetchingRights}>
            <PolarisIcon icon="submit" height={12} />
            {fetchingRights ? 'Validating Rights...' : 'Validate Rights and Submit'}
          </OverflowMenuItem>
        )}
        {isDraft && (
          <OverflowMenuItem onClick={handleDelete}>
            <PolarisIcon icon="delete" height={12} />
            Delete
          </OverflowMenuItem>
        )}
      </OverflowMenu>
      <EditIncrementalLaunchModal
        isOpen={isEditIncrementalLaunchModalOpen}
        mode={launchMode}
        launch={row}
        versionScheduling={versionScheduling}
        onClose={() => setIsEditIncrementalLaunchModalOpen(false)}
      />
    </>
  );
};

export default AvailScheduleRowExpandOverflowActions;
